import { Injectable, inject, signal } from '@angular/core';

import { SnackBarService } from '@gde-mit/angular-design-system';

import type { DocumentVisibility } from '@api';

import { TranslocoService } from '@jsverse/transloco';

import { DocumentsApiService } from '../api/documents.api.service';

const LIST_I18N_PREFIX = 'DOCUMENT_LIBRARY.LIST';

export type DocumentLibraryVisibilityBulkUpdateOptions = {
  readonly updates: readonly { readonly id: string; readonly visibility: DocumentVisibility }[];
  readonly onUpdated?: () => Promise<void> | void;
};

export type DocumentLibraryVisibilityUpdateOptions = {
  readonly id: string;
  readonly visibility: DocumentVisibility;
  readonly onUpdated?: () => Promise<void> | void;
};

@Injectable({ providedIn: 'root' })
export class DocumentLibraryVisibilityService {
  readonly #documentsApi = inject(DocumentsApiService);
  readonly #snackBar = inject(SnackBarService);
  readonly #transloco = inject(TranslocoService);

  readonly updatingIds = signal<ReadonlySet<string>>(new Set());

  isUpdating(id: string): boolean {
    return this.updatingIds().has(id);
  }

  async updateVisibility(options: DocumentLibraryVisibilityUpdateOptions): Promise<void> {
    const { id, visibility, onUpdated } = options;
    if (!id || this.updatingIds().has(id)) {
      return;
    }

    this.#setUpdating(id, true);
    try {
      await this.#documentsApi.updateVisibility(id, visibility);
      await onUpdated?.();
    } catch {
      this.#openVisibilityErrorSnackBar();
    } finally {
      this.#setUpdating(id, false);
    }
  }

  async updateManyVisibility(options: DocumentLibraryVisibilityBulkUpdateOptions): Promise<void> {
    const pending = options.updates.filter(({ id }) => id && !this.updatingIds().has(id));
    if (pending.length === 0) {
      return;
    }

    this.#setUpdatingMany(
      pending.map(({ id }) => id),
      true,
    );
    try {
      const results = await Promise.allSettled(
        pending.map(({ id, visibility }) => this.#documentsApi.updateVisibility(id, visibility)),
      );
      const failed = results.some((result) => result.status === 'rejected');
      if (failed) {
        this.#openVisibilityErrorSnackBar();
      }
      if (results.some((result) => result.status === 'fulfilled')) {
        await options.onUpdated?.();
      }
    } finally {
      this.#setUpdatingMany(
        pending.map(({ id }) => id),
        false,
      );
    }
  }

  #openVisibilityErrorSnackBar(): void {
    this.#snackBar.open(
      {
        message: this.#transloco.translate(`${LIST_I18N_PREFIX}.ERROR_VISIBILITY_UPDATE`),
        testId: 'document-library-list-error-visibility-update',
      },
      { duration: 'short' },
    );
  }

  #setUpdating(id: string, updating: boolean): void {
    this.#setUpdatingMany([id], updating);
  }

  #setUpdatingMany(ids: readonly string[], updating: boolean): void {
    const next = new Set(this.updatingIds());
    for (const id of ids) {
      if (updating) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    this.updatingIds.set(next);
  }
}
